import DeleteIcon from '@mui/icons-material/Delete';
import { ChangeEvent, ReactElement } from 'react';
import { Control } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { GoalFormValues, UserSignature } from 'api';
import {
  Avatar,
  Backdrop,
  BaseTextFieldController,
  DatePicker,
  Form,
  FormAttachments,
  FormCancelButton,
  FormGroup,
  FormSubmitButton,
  MediaUploadZone,
  NewConfirmDialog,
  RichEditorController,
  SelectController,
} from 'components';
import { GOAL_FIELDS_LENGTH } from 'constants/fieldsLength';
import { multiSelectStyles } from 'features/documentCategories/components/CreateDocumentCategoriesForm/styles';
import { useWindowSize } from 'hooks';
import { richEditorStyles, textFieldMobileStyles, textFieldStyles } from 'styles';
import { Attachment, SelectOption } from 'types';
import { getFullName } from 'utils';
import { required } from 'validations';

import { descriptionFieldRules, titleFieldRules } from './rules';
import {
  ButtonsW,
  DeleteButtonW,
  FormAndPreviewW,
  FromContainer,
  HeaderAuthorInfo,
  HeaderAuthorName,
  HeaderDateTime,
  HeaderLeft,
  HeaderW,
  LabelTitle,
} from './styled';
import { avatarSx, selectSx } from './styles';

export interface GoalFormFieldsProps {
  isEdit: boolean;
  isDetailsLoaded?: boolean;
  control: Control<GoalFormValues>;
  statusOptions: SelectOption[];
  assigneeOptions: SelectOption[];
  attachedImages: Attachment[];
  attachedDocs: Attachment[];
  attachedVideos: Attachment[];
  isLoading: boolean;
  isOpen: boolean;
  progress: number;
  uploadEntityId?: string;
  onCancel: () => void;
  handleFormSubmit: () => void;
  handleUploadAll: (files: FileList, post_id?: string) => Promise<void>;
  handleDeleteAttachment: (attachmentId: string, type: 'images' | 'docs' | 'videos') => void;
  handleSubmitConfirmModal: () => void;
  handleCloseConfirmModal: () => void;
  handleAssigneeChange: (e: ChangeEvent<HTMLInputElement>) => void;
  isDirty?: boolean;
  author?: UserSignature;
  createdAt?: string;
  isAssigneeFieldDisabled?: boolean;
  dataEditable?: boolean;
  handleRemoveClick?: () => void;
}

export function GoalFormFields({
  isEdit,
  isDetailsLoaded,
  control,
  statusOptions,
  assigneeOptions,
  attachedImages,
  attachedDocs,
  attachedVideos,
  isLoading,
  isOpen,
  progress,
  uploadEntityId,
  onCancel,
  handleFormSubmit,
  handleUploadAll,
  handleDeleteAttachment,
  handleSubmitConfirmModal,
  handleCloseConfirmModal,
  handleAssigneeChange,
  isDirty,
  author,
  createdAt,
  isAssigneeFieldDisabled,
  dataEditable,
  handleRemoveClick,
}: GoalFormFieldsProps): ReactElement {
  const { t } = useTranslation();
  const { isMobile } = useWindowSize();

  const formGroupMarginBottom = isMobile ? 8 : isEdit ? 36 : 16;
  const fieldStyles = isMobile ? textFieldMobileStyles : textFieldStyles;
  const showContent = !isEdit || isDetailsLoaded;

  return (
    <>
      {isLoading && <Backdrop progress={progress} />}

      {showContent && (
        <FormAndPreviewW>
          <FromContainer>
            {isEdit && author && (
              <HeaderW>
                <HeaderLeft>
                  <Avatar
                    size={isMobile ? 'xs' : 's'}
                    userId={author.id}
                    filename={author.avatar?.small || ''}
                    wrapperSx={avatarSx}
                  />
                  <HeaderAuthorInfo>
                    <HeaderAuthorName>
                      {getFullName({ firstName: author.first_name, lastName: author.last_name })}
                    </HeaderAuthorName>
                    {createdAt && <HeaderDateTime>{createdAt}</HeaderDateTime>}
                  </HeaderAuthorInfo>
                </HeaderLeft>

                {handleRemoveClick && (
                  <DeleteButtonW
                    onClick={handleRemoveClick}
                    startIcon={<DeleteIcon />}
                    variant="outlined"
                  >
                    {t('career.goals.remove.removeButton')}
                  </DeleteButtonW>
                )}
              </HeaderW>
            )}

            <Form onSubmit={handleFormSubmit}>
              <FormGroup marginBottom={formGroupMarginBottom}>
                {!isEdit && <LabelTitle>{t('career.goals.form.placeholders.title')}</LabelTitle>}
                <BaseTextFieldController
                  name="title"
                  control={control}
                  placeholder={t('career.goals.form.placeholders.title')}
                  sx={fieldStyles}
                  maxLength={GOAL_FIELDS_LENGTH.title.max}
                  rules={titleFieldRules}
                />
              </FormGroup>

              <FormGroup marginBottom={formGroupMarginBottom}>
                {!isEdit && <LabelTitle>{t('career.goals.form.placeholders.status')}</LabelTitle>}
                <SelectController
                  name="status"
                  control={control}
                  placeholder={t('career.goals.form.placeholders.status')}
                  options={statusOptions}
                  rules={required}
                  type={isEdit ? undefined : 'status'}
                  disabled={isEdit ? !dataEditable : false}
                  sx={isEdit ? selectSx : undefined}
                />
              </FormGroup>

              <FormGroup marginBottom={formGroupMarginBottom}>
                {!isEdit && (
                  <LabelTitle>{t('equipments.create.form.placeholders.assignee')}</LabelTitle>
                )}
                <SelectController
                  name="assignee"
                  control={control}
                  placeholder={t('equipments.create.form.placeholders.assignee')}
                  options={assigneeOptions}
                  multipleSelect
                  sx={multiSelectStyles(isMobile)}
                  handleChange={handleAssigneeChange}
                  type="poll"
                  disabled={isEdit ? isAssigneeFieldDisabled : false}
                />
              </FormGroup>

              <FormGroup
                marginBottom={formGroupMarginBottom}
                row={isEdit}
                sx={{ flexWrap: isMobile ? 'wrap' : 'nowrap', columnGap: '24px' }}
              >
                {!isEdit && (
                  <LabelTitle>{t('career.goals.form.placeholders.description')}</LabelTitle>
                )}
                <RichEditorController
                  name="description"
                  control={control}
                  placeholder={t('career.goals.form.placeholders.description')}
                  maxLength={GOAL_FIELDS_LENGTH.description.max}
                  rules={descriptionFieldRules}
                  sx={richEditorStyles(isMobile)}
                />
              </FormGroup>

              <FormGroup marginBottom={16}>
                {!isEdit && <LabelTitle>{t('career.goals.deadline')}</LabelTitle>}
                <ButtonsW>
                  <DatePicker
                    name="deadline"
                    control={control}
                    placeholder={t('career.goals.deadline')}
                    sx={fieldStyles}
                  />
                </ButtonsW>
              </FormGroup>

              <FormAttachments
                attachedImages={attachedImages}
                attachedDocs={attachedDocs}
                attachedVideos={attachedVideos}
                handleDeleteAttachment={handleDeleteAttachment}
              />
              <MediaUploadZone onUpload={(files) => handleUploadAll(files, uploadEntityId)} />

              {(isEdit || !isMobile) && (
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: '12px',
                    marginTop: '32px',
                  }}
                >
                  <FormCancelButton variant="outlined" onClick={onCancel}>
                    {t('common.cancel')}
                  </FormCancelButton>
                  <FormSubmitButton type="submit" disabled={isEdit ? !isDirty : false}>
                    {isEdit ? t('common.save') : t('common.create')}
                  </FormSubmitButton>
                </div>
              )}
            </Form>

            {!isEdit && isMobile && (
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  gap: '12px',
                  marginTop: '16px',
                }}
              >
                <FormCancelButton variant="outlined" onClick={onCancel}>
                  {t('common.cancel')}
                </FormCancelButton>
                <FormSubmitButton onClick={handleFormSubmit}>{t('common.create')}</FormSubmitButton>
              </div>
            )}
          </FromContainer>
        </FormAndPreviewW>
      )}

      <NewConfirmDialog
        title={t('career.goals.create.modal.title')}
        subTitle={t('career.goals.create.modal.exit')}
        open={isOpen}
        onConfirm={handleSubmitConfirmModal}
        onClose={handleCloseConfirmModal}
        submitLabel={t('career.goals.create.modal.successButton')}
        cancelLabel={t('career.goals.create.modal.cancelButton')}
      />
    </>
  );
}
